import { Toast } from 'vant'
import { IS_LOCAL } from '@/config/display/auth.config'
import SparkMD5 from 'spark-md5'
import loadImage from 'blueimp-load-image'
import { NOTICE_ID, NOTICE_STATUS } from '@/config/public.config'
import dayjs from 'dayjs'
import AMap from '@/libs/common/AMap'

/**
 * @description: 时间格式化
 * @param {*} start 开始时间
 * @param {*} end 结束时间
 * @param {*} detail 是否需要详细时间
 * @return {*}
 */
export const datetimeRangeFormat = (start, end, detail = false) => {
  try {
    const date = dayjs(start).format('YYYY-MM-DD')
    const startTime = dayjs(start).format('HH:mm')
    const endTime = dayjs(end).format('HH:mm')
    const timeText = detail ? `${date} ${startTime}-${endTime}` : date
    return timeText
  } catch (error) {
    return '-'
  }
}

export const datetimeFormat = (datetime, fm = 'YYYY-MM-DD HH:mm:ss') => {
  return datetime ? dayjs(datetime).format(fm) : '-'
}

// loading fangfa
export const loading = (msg = null) => {
  return Toast.loading({
    message: msg || '加载中...',
    forbidClick: true, // 禁用背景点击
    duration: 0, // 设置为 0 时持续展示
  })
}

/**
 * @description: 获取当前定位坐标
 * @return {Object} 
 * @example: { lat, lng }
 */
export const getCurrentLocation = async () => {
  try {
    const mapInstance = new AMap()
    const { position } = await mapInstance.getCurrentLocation()
    return {  lat: position.lat, lng: position.lng }
  } catch (e) {
    throw new Error('获取当前坐标失败：' + e)
  }
}

/**
 * 使用 spark-md5 生成文件MD5摘要
 * @resolve {string} md5
 */
export const sparkMD5 = file => {
  return new Promise((resolve, reject) => {
    const blobSlice = File.prototype.slice
    const chunkSize = 2097152 // Read in chunks of 2MB
    const chunks = Math.ceil(file.size / chunkSize)
    const spark = new SparkMD5.ArrayBuffer()
    const fileReader = new FileReader()
    let currentChunk = 0

    fileReader.onload = function (e) {
      spark.append(e.target.result) // Append array buffer
      currentChunk++

      if (currentChunk < chunks) {
        loadNext()
      } else {
        resolve(spark.end())
      }
    }

    fileReader.onerror = function (e) {
      reject(e)
    }

    function loadNext() {
      const start = currentChunk * chunkSize
      const end = start + chunkSize >= file.size ? file.size : start + chunkSize
      fileReader.readAsArrayBuffer(blobSlice.call(file, start, end))
    }

    loadNext()
  })
}

// 转化图片为jpg格式
export const processImage = ({ file }) => {
  const fileName = file.name.slice(0, file.name.lastIndexOf('.'))
  // const fileName = generateUuid();
  return new Promise(resolve => {
    loadImage(
      file,
      function (canvas) {
        canvas.toBlob(blob => {
          blob.path = URL.createObjectURL(blob)
          const file = new window.File([blob], `${fileName}.jpg`, {
            type: 'image/jpeg',
          })
          resolve(file)
        }, 'image/jpeg')
      },
      {
        orientation: true,
        canvas: true, // callback 返回 canvas
        meta: true, // callback 返回 data
        maxWidth: 1920,
      },
    )
  })
}

export const processImage2 = file => {
  const fileName = file.name.slice(0, file.name.lastIndexOf('.'))
  // const fileName = generateUuid();
  return new Promise(resolve => {
    loadImage(
      file,
      function (canvas) {
        canvas.toBlob(blob => {
          blob.path = URL.createObjectURL(blob)
          const file = new window.File([blob], `${fileName}.jpg`, {
            type: 'image/jpeg',
          })
          resolve(file)
        }, 'image/jpeg')
      },
      {
        orientation: true,
        canvas: true, // callback 返回 canvas
        meta: true, // callback 返回 data
        maxWidth: 1920,
      },
    )
  })
}

/**
 * @description: 图片处理和MD5方法的组合
 * @param {*} file
 * @return {*} 处理完的图片和MD5
 */
export const processImageMD5 = async file => {
  const newFile = await processImage(file)
  const md5 = await sparkMD5(newFile)
  return { newFile, md5 }
}

/**
 * @description: 验证对象中是否存在某个属性，加下划线跟 hasOwn 区分，不用hasOwn的原因是因为部分机型不支持
 * @param {*} obj
 * @param {*} name
 * @return {*}
 */
export const _hasOwn = (obj, name) => {
  return Object.prototype.hasOwnProperty.call(obj, name)
}

/**
 * @description: 将图片http链接转为https
 * @param {*} img
 * @return {*}
 */
export const httpToHttps = img => {
  let httpsList = []
  if (Array.isArray(img)) {
    httpsList = img.map(v => v.replace(/^http:\/\//, 'https://'))
    return httpsList
  } else if (typeof img === 'string') return img.replace(/^http:\/\//, 'https://')
  return null
}

/**
 * @description: 确认 query 是否有 idt_notice_id 或 idt_notice_status，有就返回，没有返回空对象
 * @param {*} query
 * @return {Object}
 */
export const queryFormat = query => {
  let queryObject = {}
  for (const queryItem of [NOTICE_ID, NOTICE_STATUS]) {
    if (_hasOwn(query, queryItem)) queryObject[queryItem] = query[queryItem]
  }
  return queryObject
}

/**
 * @description: blob 转 base64
 * @param {*} file
 * @param {*} callback
 * @return {*}
 */
export const blobToBase64 = ({ file }, callback) => {
  let r = new FileReader()
  r.onload = function (e) {
    if (typeof callback === 'function') callback(e.target.result)
    else console.log('base64: ', e.target.result)
  }
  if (file.type === 'image/jpeg') r.readAsDataURL(file)
  else if (typeof file === 'string') callback(file)
}

/**
 * @description: base64 转 blob
 * @param {*} file
 * @param {*} callback
 * @return {*}
 */
export const Base64ToBlob = ({ file }) => {
  try {
    let ba64 = file.split(','),
      mime = ba64[0].match(/:(.*?);/)[1],
      bstr = window.atob(ba64[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
  } catch (e) {
    console.log('e', e)
  }
}
